import React, { useState, useRef, useEffect } from "react";
import { FaPaperPlane } from "react-icons/fa";
import { BsCheckLg, BsCheckAll } from "react-icons/bs";
import { FiMessageSquare } from "react-icons/fi";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import api from "@/services/api";
import useStore from "@/services/store";
import i18n from "@/locales";

const translations = {
  en: {
    type_message_placeholder: "Write your message...",
    send: "Send",
    sending: "Sending...",
    message_sent: "Message sent successfully",
    error_sending: "Error sending message",
    no_messages: "No messages yet. Start a conversation!",
    you: "You",
    today: "Today",
    yesterday: "Yesterday",
  },
  fr: {
    type_message_placeholder: "Écrivez votre message...",
    send: "Envoyer",
    sending: "Envoi...",
    message_sent: "Message envoyé avec succès",
    error_sending: "Erreur lors de l'envoi du message",
    no_messages: "Aucun message pour le moment. Démarrez une conversation !",
    you: "Vous",
    today: "Aujourd'hui",
    yesterday: "Hier",
  },
};

export default function ConversationTab({ candidate, application }) {
  const { t } = useComponentTranslation(translations);
  const { user } = useStore();
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = async () => {
    try {
      const obj = { user_id: candidate._id };
      if (application) obj.application_id = application._id;
      const { ok, data } = await api.post("/conversation/search", obj);
      if (!ok) throw new Error(data.message);
      setConversation(data[0]);

      const { ok: okMessages, data: dataMessages } = await api.post("/message/search", { conversation_id: data[0]?._id });
      if (!okMessages) throw new Error(dataMessages.message);
      setMessages(dataMessages);
    } catch (e) {
      console.error("Error fetching conversations:", e);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [candidate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      let conv = conversation;
      if (!conv) {
        const {
          ok: okConversation,
          data: dataConversation,
          code: codeConversation,
        } = await api.post("/conversation", {
          target_user_id: candidate._id,
          application_id: application?._id,
        });
        if (!okConversation) throw new Error(codeConversation);
        conv = dataConversation;
        setConversation(conv);
      }

      const { ok, data, code } = await api.post("/message", {
        conversation_id: conv._id,
        content: message.trim(),
        type: "text",
      });
      if (!ok) throw new Error(code);
      setMessage("");
      setMessages((prev) => [...prev, data]);
    } catch (e) {
      console.error("Error sending message:", e);
      toast.error(t("error_sending"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const groupMessagesByDate = (msgs = []) => {
    const groups = {};
    msgs.forEach((message) => {
      const date = new Date(message.created_at || message.timestamp).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="flex flex-col h-full max-h-[calc(90vh-300px)]">
      {/* Messages container */}
      <div className="flex-1 overflow-y-auto mb-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <FiMessageSquare className="w-12 h-12 mb-4 text-gray-400" />
            <p className="text-sm">{t("no_messages")}</p>
          </div>
        ) : (
          <>
            {Object.entries(messageGroups).map(([date, msgs]) => (
              <div key={date} className="mb-6">
                <div className="text-center mb-4">
                  <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-full inline-block">
                    <MessageDate date={date} t={t} />
                  </span>
                </div>
                {msgs.map((msg, index) => {
                  const isCurrentUser = msg.sender_id?.toString() === user._id?.toString();
                  return (
                    <div key={index} className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} mb-4`}>
                      <div className={`max-w-[70%] rounded-lg p-3 ${isCurrentUser ? "bg-primary text-white" : "bg-gray-100 text-gray-900"}`}>
                        <pre className="text-sm whitespace-pre-wrap wrap-break-word font-inter">{msg.content}</pre>
                        <div className={`flex items-center gap-1 mt-1 ${isCurrentUser ? "justify-end" : "justify-start"}`}>
                          <span className={`text-xs ${isCurrentUser ? "text-white/75" : "text-gray-500"}`}>
                            {new Date(msg.created_at).toLocaleTimeString(i18n.language || "en", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                          {isCurrentUser && <MessageStatus status={msg.status} />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message input form */}
      <form onSubmit={handleSubmit} className="border-t border-gray-200 pt-4">
        <div className="flex items-end gap-2">
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("type_message_placeholder")} className="input flex-1" disabled={isSubmitting} />
          <button type="submit" disabled={isSubmitting || !message.trim()} className="btn btn-primary">
            {t("send")}
            <FaPaperPlane className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}

const MessageStatus = ({ status }) => {
  switch (status) {
    case "sent":
      return <BsCheckLg className="text-white/60" />;
    case "delivered":
      return <BsCheckAll className="text-white/60" />;
    case "read":
      return <BsCheckAll className="text-white" />;
    default:
      return null;
  }
};

const MessageDate = ({ date, t }) => {
  const messageDate = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (messageDate.toDateString() === today.toDateString()) {
    return t("today");
  } else if (messageDate.toDateString() === yesterday.toDateString()) {
    return t("yesterday");
  } else {
    return messageDate.toLocaleDateString(i18n.language || "en", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
};
